import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";

export type WeeklyAttendanceEvent = {
  eventId: string;
  eventName: string;
  attendanceCount: number;
  eventDate?: string | Date | null;
};

export type WeeklyAttendanceReportContext = {
  asOf: Date;
  events: WeeklyAttendanceEvent[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * Without coercion, a single non-finite upstream `attendanceCount` poisons
 * `totalAttendance` (NaN propagates through `+`), and a string upstream value
 * coerces the whole sum to a concatenated string ("12" + 3 → "123").
 */
function toFiniteNonNegativeInt(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  // Counts cannot be negative or fractional; clamp to non-negative integer.
  return Math.max(0, Math.trunc(n));
}

/** Render an event name with fallback so a blank label never reaches the UI. */
function formatEventName(value: unknown): string {
  if (typeof value !== "string") return "Unknown Event";
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "Unknown Event";
}

/**
 * Convert an eventDate input to a stable sortable epoch-ms.
 * - `Date` instances: their `getTime()` (NaN if invalid).
 * - ISO strings: parsed via `new Date(...)`; non-finite parses fall through.
 * - Anything else (null/undefined/garbage): -Infinity (sorts last for desc).
 */
function eventDateToMs(value: string | Date | null | undefined): number {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : -Infinity;
  }
  if (typeof value === "string" && value.length > 0) {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : -Infinity;
  }
  return -Infinity;
}

export function buildWeeklyAttendanceReportAggregation({
  asOf,
  events,
}: WeeklyAttendanceReportContext): ReportAggregation {
  // Defensive: callers (and snapshot serializers) sometimes pass `undefined`
  // when the upstream query is still loading. The previous `events.map(...)`
  // would throw and crash the whole report shell.
  const safeEvents = Array.isArray(events) ? events : [];

  // Dedupe by eventId. Wiring synthesises an id from `event_id || event_name
  // || "unknown"`, but two different upstream paths (or two adapters sharing
  // a query) can still feed in duplicates. Without dedupe, React would emit
  // duplicate-key warnings and the "Events" summary would over-count.
  const byEventId = new Map<string, {
    id: string;
    label: string;
    eventDate: string | Date | null;
    attendanceCount: number;
    _eventDateMs: number;
  }>();

  for (const event of safeEvents) {
    if (!event) continue;
    const id = typeof event.eventId === "string" && event.eventId.length > 0
      ? event.eventId
      : "unknown";
    const attendanceCount = toFiniteNonNegativeInt(event.attendanceCount);
    const existing = byEventId.get(id);
    if (existing) {
      existing.attendanceCount += attendanceCount;
      // Preserve the earliest defined eventDate so the displayed "when" is stable
      // across refetches that may shuffle row order upstream.
      const incomingMs = eventDateToMs(event.eventDate ?? null);
      if (incomingMs > -Infinity && incomingMs < existing._eventDateMs) {
        existing.eventDate = event.eventDate ?? null;
        existing._eventDateMs = incomingMs;
      }
      continue;
    }
    byEventId.set(id, {
      id,
      label: formatEventName(event.eventName),
      eventDate: event.eventDate ?? null,
      attendanceCount,
      _eventDateMs: eventDateToMs(event.eventDate ?? null),
    });
  }

  // Stable, locale-independent sort: highest attendance first (the natural
  // read order for a weekly summary), then most-recent event date, then label,
  // then id. `<`/`>` rather than `localeCompare` keeps order identical across
  // locales / Node vs. browser ICU builds.
  const rows = Array.from(byEventId.values())
    .sort((a, b) => {
      if (a.attendanceCount !== b.attendanceCount) {
        return b.attendanceCount - a.attendanceCount;
      }
      if (a._eventDateMs !== b._eventDateMs) {
        return b._eventDateMs - a._eventDateMs;
      }
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    })
    .map((event) => ({
      id: event.id,
      label: event.label,
      eventDate: event.eventDate,
      attendanceCount: event.attendanceCount,
      entityType: "event" as const,
      // Encode the id segment so a synthetic id derived from `event_name` (when
      // `event_id` is null upstream) doesn't break the URL with spaces or `&`.
      drilldownUrl: `/events/${encodeURIComponent(event.id)}`,
    }));

  // Single-pass aggregation through the same coercion used per-row, so the
  // summary cannot disagree with the visible rows when an upstream value is
  // non-finite or string-typed.
  const totalAttendance = rows.reduce((sum, row) => sum + row.attendanceCount, 0);

  return {
    asOf,
    summary: [
      buildReportSummaryItem("report.weekly_attendance.event_count", rows.length),
      buildReportSummaryItem("report.weekly_attendance.total_attendance", totalAttendance),
    ],
    rows,
  };
}
